// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/tab-navigation.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import {
  TabsList as TabsListV1,
  TabsTrigger as TabsTriggerV1,
} from "@repo/design-system/components/ui/tabs";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps } from "react";

/**
 * v2 Tab Navigation — restyles v1's `Tabs` family
 * (`components/ui/tabs.tsx`) to match the Figma "Remote Leverage — Design
 * System" file, "Tab Navigation" component node `380:763` (individual Tab
 * states at `223:19153`/`223:19154`, the reused count badge at
 * `223:19157`). Radix's `Tabs.Root` context, keyboard navigation
 * (arrow-key roving tabindex), `data-state`/`data-orientation` management,
 * and the `TabsContent` panel semantics all stay v1's own — this file only
 * overrides the visual layer.
 *
 * `Tabs` (the root context provider) and `TabsContent` (the panel — Figma
 * has no spec for panel content, only the tab bar itself) need no visual
 * override at all, so they're re-exported unstyled below, the same way
 * `breadcrumbs.tsx` re-exports the outer `Breadcrumb` nav wrapper verbatim.
 * `TabsList` is where the restyle actually happens (bg/padding/rounding
 * removed — Figma's tab bar is a plain row with an 8px gap, not v1's
 * pill/segmented-control treatment) and carries the `ds-v2` class that
 * scopes this file's tokens; `TabsTrigger` references those tokens as a
 * descendant (CSS custom properties cascade to descendants regardless of
 * whether the descendant repeats the class — same approach
 * `breadcrumbs.tsx` uses for `BreadcrumbLink`/`BreadcrumbPage`/etc. under
 * `BreadcrumbList`).
 *
 * The active/underline state is Radix-driven (`data-state="active"` on
 * `TabsTrigger`, already exposed by v1) — targeted here with
 * `data-[state=active]:` variants per the v2 convention of not turning a
 * library-driven state into a new prop. `hover:` covers the one browser-
 * native state Figma's ticket calls out that has no dedicated node: an
 * inactive tab's text moves toward the active color on hover, using the
 * same active-text token rather than inventing an undesigned third color.
 *
 * `TabBadge` is a new addition (no v1 equivalent) for the small pill count
 * shown next to a tab's label in Figma's "Tab" sub-component. It's kept
 * colocated here — not built as its own `v2/badge.tsx`-based composite —
 * because it's a one-off visual (fixed 20px circle, secondary background)
 * specific to this component's Figma node, the same way `card.tsx` keeps
 * `CardHeader`/`CardContent`/etc. together with `Card` in one file rather
 * than splitting every sub-part out. It carries its own `ds-v2` class so
 * it resolves its tokens even when used standalone.
 *
 * Deliberately out of scope:
 * - A `disabled` tab state has no Figma node in this component — v1's own
 *   `disabled:pointer-events-none disabled:opacity-50` is left untouched.
 * - Focus-visible: no Figma node, and v1's outline + ring are removed (see
 *   `TabsTrigger`). Radix activates the tab that receives focus, so the
 *   active underline doubles as the keyboard focus indicator.
 * - Vertical/side-oriented tabs: Figma's node only shows a horizontal bar,
 *   matching this ticket's "horizontal tab bar" scope.
 */

function TabsList({ className, ...props }: ComponentProps<typeof TabsListV1>) {
  return (
    <TabsListV1
      className={cn(
        "ds-v2 h-auto w-fit items-center justify-start gap-2 rounded-none border-none bg-transparent p-0",
        className
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsTriggerV1>) {
  return (
    <TabsTriggerV1
      className={cn(
        "h-auto flex-none items-center justify-center gap-1.5 whitespace-nowrap rounded-none border-0 border-transparent border-b-2 bg-transparent px-2 pt-[10px] pb-3 font-medium text-(--v2-tab-nav-text-inactive) text-[16px]/[1.48] shadow-none transition-colors",
        "hover:text-(--v2-tab-nav-text-active)",
        // No focus-visible styling: Chrome matches :focus-visible when a Radix
        // tab is clicked, so v1's ring (and the tint that replaced it) stuck to
        // the clicked tab. Radix activates on focus, so the focused tab is
        // always the active one and the underline already marks it.
        "focus-visible:outline-none focus-visible:ring-0",
        "data-[state=active]:border-(--v2-tab-nav-underline) data-[state=active]:bg-transparent data-[state=active]:text-(--v2-tab-nav-text-active) data-[state=active]:shadow-none",
        "dark:border-transparent dark:bg-transparent dark:text-(--v2-tab-nav-text-inactive) dark:data-[state=active]:border-(--v2-tab-nav-underline) dark:data-[state=active]:bg-transparent dark:data-[state=active]:text-(--v2-tab-nav-text-active)",
        className
      )}
      {...props}
    />
  );
}

function TabBadge({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "ds-v2 inline-flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-(--v2-tab-nav-badge-bg) px-1 font-medium text-(--v2-tab-nav-badge-text) text-xs leading-4",
        className
      )}
      data-slot="tab-navigation-badge"
      {...props}
    />
  );
}

// biome-ignore lint/performance/noBarrelFile: re-exporting v1's unstyled Tabs/TabsContent verbatim, not a barrel of unrelated modules
export { Tabs, TabsContent } from "@repo/design-system/components/ui/tabs";
export { TabBadge, TabsList, TabsTrigger };
